import React from 'react';
import PropTypes from 'prop-types';
import styled from '@mui/material/styles/styled';
import Typography from '@mui/material/Typography';
import { useSpring, animated } from '@react-spring/web';
import Loader from './Loader';
import { lighten } from '@mui/material/styles';
import { cx } from 'cva';

const StyledButton = styled('button', {
  shouldForwardProp: (prop) =>
    !['invertColors', 'width', 'loading', 'wide'].includes(prop),
})(({ theme, disabled, invertColors, width, loading, wide }) => {
  const bgColor = loading
    ? lighten('#049aad', 0.8)
    : '#33CCCC';

  return {
    fontSize: '1rem',
    border: '1px solid',
    borderRadius: 28,
    padding: wide ? theme.spacing(0.5, 6) : theme.spacing(0.5, 2.5),
    position: 'relative',
    ...(disabled
      ? {
          backgroundColor: '#fff !important',
          borderColor: 'rgba(0, 0, 0, 0.3)',
          cursor: 'not-allowed !important',
        }
      : {
          backgroundColor: invertColors
            ? '#fff !important'
            : `${bgColor} !important`,
          borderColor: bgColor,
          cursor: loading ? 'not-allowed' : 'pointer',
        }),
    ...(width ? { width } : {}),
    '&:hover': {
      ...(!disabled &&
        !loading && {
          backgroundColor: invertColors
            ? `#33CCCC !important`
            : '#fff !important',
          '& .styled-btn-text': {
            color: invertColors
              ? '#fff !important'
              : `#33CCCC !important`,
          },
        }),
    },
    '&:focus-visible': {
      outline: 'none',
    },
    '& .styled-btn-text': {
      ...(disabled
        ? {
            color: `${
              'rgba(0, 0, 0, 0.3)'
            } !important`,
          }
        : {
            color: invertColors
              ? `#33CCCC !important`
              : '#fff !important',
            fontWeight: 600,
          }),
    },
    [theme.breakpoints.down('md')]: {
      padding: '2px 16px',
    },
    '& .loader-container': {
      position: 'absolute',
      top: 'calc(50% - 12px)',
      left: 'calc(50% - 12px)',
    },
  };
});

function StyledContainedButton(props) {
  const {
    invertColors = false,
    browse = false,
    loading = null,
    variant = 'button',
    onClick = () => {},
    children,
    ...rest
  } = props;

  const [showLoader, setShowLoader] = React.useState(false);

  React.useEffect(() => {
    if (loading) {
      setShowLoader(true);
    } else {
      setShowLoader(false);
    }
  }, [loading]);

  const handleClick = (...args) => {
    if (loading) {
      return;
    }
    onClick(args);
  };

  const fadeOutProps = useSpring({ opacity: showLoader ? 1 : 0 });

  if (browse) {
    return (
      <button onClick={handleClick} className={cx(rest.className, 'w-full border-none border-0')}>
        <Typography
          variant={variant}
          className="styled-btn-text text-primary text-16"
          sx={{ opacity: showLoader ? 0 : 1 }}>
          {children}
        </Typography>
        {loading !== null && (
          <animated.div className="loader-container" style={fadeOutProps}>
            <Loader />
          </animated.div>
        )}
      </button>
    );
  }

  return (
    <StyledButton loading={loading} onClick={handleClick} {...rest}>
      <Typography
        variant={variant}
        className="styled-btn-text"
        sx={{ opacity: showLoader ? 0 : 1 }}>
        {children}
      </Typography>
      {loading !== null && (
        <animated.div className="loader-container" style={fadeOutProps}>
          <Loader />
        </animated.div>
      )}
    </StyledButton>
  );
}


export default StyledContainedButton;
